import React, { useImperativeHandle, useMemo, useRef, useState } from "react";
import classnames from "classnames";

import CompactSelect from "@vahak/core-ui/dist/components/CompactSelect";
import { SelectOnChangeType, SelectOption } from "@vahak/core-ui/dist/components/Select";
import useMediaQuery from "@vahak/core-ui/dist/hooks/useMediaQuery";

import FieldShellStyles from "../MarketplaceLoadFilterCard.module.scss";
import {
    createQuoteFormLocationControl,
    createQuoteFormLocationInput,
    createQuoteFormLocationMenuAlways,
    createQuoteFormLocationPlaceholder,
    createQuoteFormLocationSingleValue,
    createQuoteFormNoOptionsMessage,
    createQuoteFormStateOption,
    filterMarketplaceStateOption,
    hiddenMultiValue
} from "./marketplaceQuoteFormFieldParts";
import { MARKETPLACE_STATE_OPTIONS } from "./marketplaceFilterStateOptions";
import MarketplaceFilterMobileSheet from "./MarketplaceFilterMobileSheet";

interface MarketplaceFilterStateSingleFieldProps {
    id: string;
    placeholder: string;
    icon: React.ReactNode;
    value?: SelectOption<number>;
    mobileTitle: string;
    onChange: (value?: SelectOption<number>) => void;
}

export type MarketplaceFilterFieldFocusHandle = {
    focus: () => void;
};

const MarketplaceFilterStateSingleField = React.forwardRef<
    MarketplaceFilterFieldFocusHandle,
    MarketplaceFilterStateSingleFieldProps
>(({ id, placeholder, icon, value, mobileTitle, onChange }, ref) => {
    const isMobileScreen = useMediaQuery({ queryType: "mobile" });
    const [mobileOpen, setMobileOpen] = useState(false);
    const selectRef = useRef<any>(null);

    useImperativeHandle(ref, () => ({
        focus: () => {
            if (isMobileScreen) {
                setMobileOpen(true);
                return;
            }

            selectRef.current?.focus?.();
        }
    }));

    const handleChange: SelectOnChangeType = (option) => {
        const selected = option as SelectOption<number> | null;
        onChange(selected ?? undefined);
    };

    const components = useMemo(
        () => ({
            DropdownIndicator: () => null,
            Control: createQuoteFormLocationControl(FieldShellStyles, icon),
            Input: createQuoteFormLocationInput(),
            Placeholder: createQuoteFormLocationPlaceholder(FieldShellStyles),
            SingleValue: createQuoteFormLocationSingleValue(FieldShellStyles),
            Menu: createQuoteFormLocationMenuAlways(FieldShellStyles),
            Option: createQuoteFormStateOption(FieldShellStyles),
            NoOptionsMessage: createQuoteFormNoOptionsMessage(FieldShellStyles, () => "No matching state"),
            MultiValue: hiddenMultiValue
        }),
        [icon]
    );

    return (
        <div
            className={classnames(
                FieldShellStyles.fieldWrap,
                FieldShellStyles.fieldWrapSelect,
                FieldShellStyles.fieldWrapSearchable
            )}
        >
            <CompactSelect
                ref={selectRef}
                id={id}
                name={id}
                option={MARKETPLACE_STATE_OPTIONS}
                value={value ?? null}
                placeholder={placeholder}
                isClearable={!isMobileScreen}
                isSearchable
                openMenuOnClick
                openMenuOnFocus
                filterOption={filterMarketplaceStateOption}
                menuPosition="absolute"
                onChange={handleChange}
                components={components}
                {...(isMobileScreen && {
                    menuIsOpen: false,
                    onMenuOpen: () => {
                        selectRef.current?.blur?.();
                        setMobileOpen(true);
                    }
                })}
            />

            {mobileOpen ? (
                <MarketplaceFilterMobileSheet
                    title={mobileTitle}
                    options={MARKETPLACE_STATE_OPTIONS}
                    value={value ? [value] : []}
                    onChange={(next) => onChange(next[0])}
                    onClose={() => setMobileOpen(false)}
                />
            ) : null}
        </div>
    );
});

export default MarketplaceFilterStateSingleField;
